import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { CheckPicker } from 'rsuite'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'

import { useApi } from '../../../services/useApi'
import { getPickerValue, handlePickerChange } from './util'
import { Supplier } from '../../../utils/types/Product'
import useUrls from '../../../services/useUrls'
import { getSupplierDeliveryDetails } from '../../../lib/service'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import useGlobalAnalyticsParams from '../../hooks/useGlobalAnalyticsParams'
import { FilterItem, isDefined } from '../../../utils/types/misc'
import useCyModel from '../../../services/useCyModel'
import useIsOnMobile from '../../../services/useIsOnMobile'
import CheckboxFilterMobile from '../CheckboxFilterMobile'

import styles from '../../../styles/CatalogFilters.module.less'

interface SupplierPickerProps {
  type: string
  label: string
  suppliers?: Supplier[]
  disabled?: boolean
  appliedFilters?: Record<string, FilterItem[]>
  updateAppliedFilter?: (filterKey: string, filterItem: FilterItem) => void
  removeAppliedFilter?: (filterKey: string) => void
  removeAppliedFilterItem?: (filterKey: string, value: string | number) => void
}

const SupplierPicker: FC<SupplierPickerProps> = (props) => {
  const {
    type,
    label,
    suppliers: preFilteredSuppliers,
    disabled = false,
    appliedFilters = {},
    updateAppliedFilter,
    removeAppliedFilter,
    removeAppliedFilterItem,
  } = props

  const { getResource } = useApi()
  const { pushT } = useUrls()
  const initializedRef = useRef(false)
  const isOnMobile = useIsOnMobile()
  const { query, asPath, locale: countryAndLocale } = useRouter()
  const { country } = getCountryAndLocaleStrings(countryAndLocale)

  const [globalAnalyticsParams, setGlobalAnalyticsParams] = useGlobalAnalyticsParams()
  const { filterInteractionAmount = 0 } = globalAnalyticsParams

  const filtersRef = useRef<HTMLDivElement>(null)
  const containerRef = filtersRef?.current || undefined
  const hasPrefeilteredData = preFilteredSuppliers?.length

  const filterCode = `f_${type}`

  // Get values from query params (for initial load and URL-based navigation)
  const queryParamValues = useMemo(
    () => getPickerValue(query, filterCode),
    [query, filterCode],
  )

  // Use appliedFilters for controlling the component if provided,
  // otherwise fall back to query params
  const pickerValue = useMemo(() => {
    // If appliedFilters for this filter exist, use them
    if (appliedFilters[filterCode]?.length) {
      return appliedFilters[filterCode].map((item) => item.value as string)
    }
    return []
  }, [appliedFilters, filterCode])

  const { data: suppliers } = getResource<Supplier>(
    hasPrefeilteredData ? null : 'supplier',
    'include=supplier/service&page[limit]=500',
  )

  const {
    refine: clear,
  } = useClearRefinements({
    includedAttributes: ['supplier'],
  })

  const {
    items,
    refine,
    searchForItems,
  } = useRefinementList({
    attribute: 'supplier',
    operator: 'or',
    limit: 100,
  })

  const displayedSuppliers = useMemo(() => (
    hasPrefeilteredData
      ? preFilteredSuppliers
      : suppliers
  ).filter((supp) => (
    hasPrefeilteredData
      || (getSupplierDeliveryDetails(supp, country).isAvailable
        && items.find((item) => item.label === supp['supplier.label']))
  )), [hasPrefeilteredData, preFilteredSuppliers, suppliers, country, items])

  const supplierFilterData = useMemo(() => displayedSuppliers.map((supplier) => {
    if (hasPrefeilteredData) {
      return {
        label: supplier['supplier.label'],
        value: supplier.id,
      }
    }
    const count = items.find((item) => item.label === supplier['supplier.label'])?.count || 0
    return {
      label: (
        <>
          {supplier['supplier.label']}
          <span className={`${styles.count} ${!count ? styles.disabled : ''}`}>
            {count}
          </span>
        </>
      ),
      value: supplier.id,
    }
  }), [displayedSuppliers, hasPrefeilteredData, items])

  const handleInitializeFilters = (supplierIds: string[]) => {
    if (supplierIds.length && updateAppliedFilter) {
      supplierIds.forEach((supplierId) => {
        const supplier = suppliers.find((s) => s.id === supplierId)
        if (supplier) {
          updateAppliedFilter(filterCode, {
            label: supplier['supplier.label'] || '',
            value: supplierId,
          })
        }
      })
    }
  }

  const handleOnChange = useCallback((value: string[]) => {
    initializedRef.current = true

    const isClear = value.length < pickerValue.length
    const isNew = (id: string) => !pickerValue.includes(id)

    if (isClear) {
      clear()

      // Find which suppliers were removed
      const removedSupplierIds = pickerValue.filter((id) => !value.includes(id))

      // Update applied filters state
      if (removeAppliedFilterItem) {
        removedSupplierIds.forEach((supplierId) => {
          removeAppliedFilterItem(filterCode, supplierId)
        })
      }
    }

    suppliers
      .filter((supplier) => value.includes(supplier.id) && (isClear || isNew(supplier.id)))
      .map((supplier) => supplier['supplier.label'])
      .filter(isDefined)
      .forEach(refine)

    const pickedSuppliers = supplierFilterData
      .filter((supplier) => value.includes(supplier.value))
      .map((supplier) => supplier.label)

    setGlobalAnalyticsParams({
      ...globalAnalyticsParams,
      selectedFilterValue: pickedSuppliers.join('|').toLowerCase(),
      selectedFilterCode: filterCode,
      filterInteractionAmount: Number(filterInteractionAmount) + 1,
    })

    // Update appliedFilters state in CatalogHeader with new structure
    if (updateAppliedFilter) {
      // For newly added suppliers
      const newlyAdded = value.filter(isNew)
      handleInitializeFilters(newlyAdded)
    } else if (removeAppliedFilter && value.length === 0) {
      // If all suppliers are deselected, remove entire filter key
      removeAppliedFilter(filterCode)
    }

    handlePickerChange(pushT, query, asPath, filterCode)(value)
  }, [suppliers.length, pickerValue, filterCode])

  useEffect(() => {
    if (pickerValue.length) {
      const availableSupplierIds = pickerValue.filter((value) => (
        supplierFilterData.map((supplierOption) => supplierOption.value).includes(value)
      ))

      const pickerChangeHandler = handlePickerChange(pushT, query, asPath, filterCode)
      if (availableSupplierIds.length) {
        pickerChangeHandler(availableSupplierIds)
      }
    }
  }, [suppliers.map((supplier) => supplier.id).join(',')])

  useEffect(() => {
    // Refine instantsearch filter items when loading page
    const refined = items.filter((item) => item.isRefined)
    if (!initializedRef.current && refined.length === 0 && pickerValue.length !== 0) {
      initializedRef.current = true
      suppliers
        .filter((supplier) => pickerValue.includes(supplier.id))
        .map((supplier) => supplier['supplier.label'])
        .filter(isDefined)
        .forEach((item) => {
          refine(item)
        })
    }
  }, [pickerValue, refine])

  useEffect(() => {
    // Initialize appliedFilters state from URL on page load/refresh
    if (query && queryParamValues.length > 0) {
      handleInitializeFilters(queryParamValues)
    }
  }, [query, queryParamValues])

  useCyModel('GenericPicker', {
    [filterCode]: {
      value: pickerValue,
      onChange: handleOnChange,
      options: displayedSuppliers.map((supplier) => ({
        label: supplier['supplier.label'],
        value: supplier.id,
      })),
    },
  })

  if (isOnMobile) {
    return (
      <CheckboxFilterMobile
        data={supplierFilterData}
        value={pickerValue}
        onSearch={searchForItems}
        onChange={handleOnChange}
      />
    )
  }

  return (
    <div
      ref={filtersRef}
      className="relative"
      data-testid="supplier-picker-container"
    >
      <CheckPicker
        data={supplierFilterData}
        placeholder={label}
        value={pickerValue}
        container={containerRef}
        onSearch={searchForItems}
        onChange={handleOnChange}
        block
        sticky
        placement="bottomStart"
        disabled={disabled}
        data-testid="supplier-picker"
      />
    </div>
  )
}

export default SupplierPicker
